/* eslint indent: 0 */

import Backbone from 'backbone';
import includes from 'lodash/includes';
import compact from 'lodash/compact';

import loglevel from 'loglevel';

export const ACCOUNT_ADMIN_PERMISSION_GROUP = '1';
export const CONTENT_ADMIN_PERMISSION_GROUP = '2';
export const EDITOR_PERMISSION_GROUP = '3';
export const AUTHOR_PERMISSION_GROUP = '4';

  var YES = 'yes';
  var NO = 'no';
  var ALL = 'all';
  var OWN = 'own';

  var PERMISSION_GROUP_ROLE_NAME_KEY = 'role_name';
  var INVITE_USERS_RULE = 'invite_users';
  var DELETE_USERS_RULE = 'delete_users';
  var SET_USER_ROLES_RULE = 'set_user_roles';
  var SET_USER_NAMES_RULE = 'set_user_name';
  var VIEW_PAGES_RULE = 'view_pages';
  var CREATE_PAGES_RULE = 'create_pages';
  var PUBLISH_PAGES_RULE = 'publish_pages';
  const PUBLISH_TEMPLATES_RULE = 'publish_templates';
  var ASSIGN_PAGES_RULE = 'assign_pages';
  var VIEW_ACCOUNT_INFO_RULE = 'view_account_info';
  var SET_SUPER_TEMPLATE_USAGE_RULE = 'set_super_template_usage';
  var EDIT_THEMES_RULE = 'edit_themes';
  var EDIT_VISITOR_ACCESS_SETTINGS_RULE = 'change_viewing_permissions';
  var VIEW_PAGE_HISTORY_RULE = 'view_page_history';
  var VIEW_FORMS_RULE = 'view_forms';
  var CREATE_FORMS_RULE = 'create_forms';
  var VIEW_BRANDED_EMAIL_TEMPLATES_RULE = 'view_branded_email_templates';
  var CREATE_BRANDED_EMAIL_TEMPLATES_RULE = 'create_branded_email_templates';
  var CAN_USE_PAGE_EDITOR_RULE = 'use_page_editor';
  var MANAGE_PAGE_FOLDERS_RULE = 'manage_page_folders';
  var EDIT_SHARE_BEHAVIOR_RULE = 'edit_share_behavior';
  var USE_MEDIA_LIBRARY_MANAGER_RULE = 'use_media_library_manager';
  var USE_PROMPTS_MANAGER_RULE = 'use_prompts_manager';
  var EDIT_ALL_ROUTES_RULE = 'edit_all_routes';
  var MODIFY_PRIVACY_POLICY_RULE = 'modify_privacy_policy';
  var CAN_VIEW_SITE_ANALYTICS = 'can_view_site_analytics'; // keen access
  var CAN_EDIT_PAGE_DETAILS = 'can_edit_page_details';
  var EXPORT_SITES_RULE = 'can_export_sites';
  var CAN_EDIT_OBJECT_PERMISSIONS = 'can_edit_object_permissions';

  
  var Permissions = Backbone.Model.extend({

    initialize: function(attrs, options) {
      options = options || {};
      if (!options.zfUser) {
        loglevel.warn('Creating permissions object without user. Some checks will not work.');
      }
      this.zfUser = options.zfUser;
    },
    
    isSuperUser: function() {
      return this.zfUser.get('is_zoomforth_superuser');
    },

    _checkRuleAgainstSiteOrTemplate: function(siteOrTemplate, ruleToCheck) {
      var isAllowed = false;
      if (includes(this.get(ruleToCheck), ALL)) {
        isAllowed = true;
      } 
      if(
        includes(this.get(ruleToCheck), OWN) && siteOrTemplate.isAccessibleBy(this.zfUser)
      ) {
        isAllowed = true;
      } 
      return isAllowed;
    },

    getUserRoleName: function() {
      return this.get(PERMISSION_GROUP_ROLE_NAME_KEY);
    }, 
    
    canInviteUsers: function() {
      return includes(this.get(INVITE_USERS_RULE), YES);
    },
    
    canDeleteUsers: function() {
      return includes(this.get(DELETE_USERS_RULE), YES);
    }, 
    
    canEditUsers: function () {
      return includes(this.get(SET_USER_ROLES_RULE), YES) 
          || includes(this.get(SET_USER_NAMES_RULE), YES);
    }, 

    canExportSites: function() {
      return includes(this.get(EXPORT_SITES_RULE), YES);
    }, 

    canSetUserName: function(){
      return includes(this.get(SET_USER_NAMES_RULE), YES);
    },
    
    // If this user can set the name of the user it makes sense for them to be able to update the notes as well
    canSetUserNotes: function(){
      return includes(this.get(SET_USER_NAMES_RULE), YES);
    },

    canSetUserRole: function(){
      return includes(this.get(SET_USER_ROLES_RULE), YES);
    },
    
    /**
      User can approve page edits using review system
    */
    canApproveEdits: function() {
      return this.canViewAllPages(); // && this.canPublish(page);
    },

    /**
      User can change the viewing privacy for this page
    */
    canChangeVisitorAccessSettings: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },
    
    /**
      User can change the viewing privacy for at least one page (i.e. editors can do this)
    */
    canChangeViewingPrivacyOnSomePages: function() {
      return !includes(this.get(EDIT_VISITOR_ACCESS_SETTINGS_RULE), NO);
    },

    canArchivePage: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },

    canUnarchivePage: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },

    canActivatePage: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },

    canDeactivatePage: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },
    
    canDownloadFailedAccessLogs: function(page) {
      return this._checkRuleAgainstSiteOrTemplate(page, EDIT_VISITOR_ACCESS_SETTINGS_RULE);
    },
    
    canCreatePages: function() {
      return includes(this.get(CREATE_PAGES_RULE), YES); 
    }, 

    canDeletePage: function(page) {
      return !!(
        this.canPublish(page) || 
        (this.get('created_by_user') && this.get('created_by_user') === this.zfUser.get('id'))
      );
    },

    canViewPageDetails: function(){
      return includes(this.get(CAN_EDIT_PAGE_DETAILS), YES);
    },
    
    canAssignPage: function(lens) {
      return this._checkRuleAgainstSiteOrTemplate(lens, ASSIGN_PAGES_RULE);
    }, 
    
    canViewAllPages: function() {
      return includes(this.get(VIEW_PAGES_RULE), ALL);
    },

    canViewPrivateMediaLibraryFolders: function() {
      // users that can view all pages should also be able to view all upload folders.
      // (we don't need a special permission for this behavior yet; 
      // tying it to the view pages rule is fine for now)
      return this.canViewAllPages();
    },

    isReadOnly: function(lens) {
      if (!this._checkRuleAgainstSiteOrTemplate(lens, VIEW_PAGES_RULE)) {
        return true;
      } else {
        return false;
      }
    },
    
    canViewAccountInfo: function() { 
      return (includes(this.get(VIEW_ACCOUNT_INFO_RULE), YES));
    },

    canSetSuperTemplateUsage: function() { 
      return (includes(this.get(SET_SUPER_TEMPLATE_USAGE_RULE), YES));
    },
    
    canPublish: function(siteOrTemplate) {
      return (
        siteOrTemplate.isTemplate() ?
          this._checkRuleAgainstSiteOrTemplate(siteOrTemplate, PUBLISH_TEMPLATES_RULE)
        :
          this._checkRuleAgainstSiteOrTemplate(siteOrTemplate, PUBLISH_PAGES_RULE)
      );
    }, 
    
    canPublishPagesUsingTheEditor: function() {
      return !includes(this.get(PUBLISH_PAGES_RULE), NO) && this.canUseEditor();
    },
        
    canEditPageDescription: function() {
      return true;
    },
    
    canEditThemes: function() {
      return includes(this.get(EDIT_THEMES_RULE), YES);
    },
    
    canViewPageHistory: function() { 
      return includes(this.get(VIEW_PAGE_HISTORY_RULE), YES);
    },

    canViewPageAnalytics: function(){
      return includes(this.get(CAN_VIEW_SITE_ANALYTICS), YES);
    },

    canViewPageVisits: function(){
      return includes(this.get(CAN_VIEW_SITE_ANALYTICS), YES);
    },
    
    canViewForms: function(){
      return includes(this.get(VIEW_FORMS_RULE), YES);
    },

    canCreateForms: function() {
      return includes(this.get(CREATE_FORMS_RULE), YES);
    },

    canViewBrandedEmailTemplates: function(){
      return includes(this.get(VIEW_BRANDED_EMAIL_TEMPLATES_RULE), YES);
    },

    canCreateBrandedEmailTemplates: function() {
      return includes(this.get(CREATE_BRANDED_EMAIL_TEMPLATES_RULE), YES);
    },
    
    canUseEditor() {
      return includes(this.get(CAN_USE_PAGE_EDITOR_RULE), YES); 
    },

    /**
      User can rename, delete, move and create folders.
      User can edit the folder in which a page resides.
    */
    canManagePageFolders: function() {
      return includes(this.get(MANAGE_PAGE_FOLDERS_RULE), YES);
    },

    canModifyPrivacyPolicy: function() {
      return includes(this.get(MODIFY_PRIVACY_POLICY_RULE), YES);
    },
    
    getDashboardType: function() {
      return this.get('dashboard_type');
    },
    
    getRoleDescription: function(){
      return this.get('description');
    },

    getAbilitiesDescriptionForPage(page) {

      return compact([
        this.canUseEditor() ? 'Edit' : null,
        this.canPublish(page) ? 'Publish' : null,
        'Create Link'
      ]);

    },
    
    canEditRoute: function(routeModel) {
      return (
        includes(this.get(EDIT_ALL_ROUTES_RULE), YES) ||
        routeModel.get('owner_id') === this.zfUser.get('id')
      );
    },

    canEditAllRoutes: function() {
      return includes(this.get(EDIT_ALL_ROUTES_RULE), YES);
    },
    
    canEditShareBehavior: function() {
      return includes(this.get(EDIT_SHARE_BEHAVIOR_RULE), YES);
    },
    
    canUseMediaLibraryManager: function() {
      return includes(this.get(USE_MEDIA_LIBRARY_MANAGER_RULE), YES);
    },
    
    canUsePromptsManager: function() {
      return includes(this.get(USE_PROMPTS_MANAGER_RULE), YES);
    },
    
    canManageSnippets: function() {
      return includes(this.get('manage_page_snippets'), YES);
    },

    canEditObjectPermissions: function() { 
      return (includes(this.get(CAN_EDIT_OBJECT_PERMISSIONS), YES));
    }
    
  });
  

export default Permissions;
